/**
 * TanStack Query hooks for the user profile + unified DIAN verification.
 * All mutations invalidate the ['profile'] query key on success.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import {
  getProfile,
  updateProfile,
  startVerification,
  completeVerification,
  type ProfileUpdateInput,
  type VerificationStartInput,
  type VerificationCompleteInput,
} from "./profile";

export const PROFILE_KEY = ["profile"] as const;

export function useProfile() {
  const auth = useAuth();
  return useQuery({
    queryKey: [...PROFILE_KEY, auth.user?.id ?? null],
    queryFn: getProfile,
    enabled: auth.status === "authenticated",
    staleTime: 30_000,
    retry: false,
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProfileUpdateInput) => updateProfile(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROFILE_KEY }),
  });
}

export function useStartVerification() {
  return useMutation({
    mutationFn: (input: VerificationStartInput) => startVerification(input),
    // Don't invalidate profile yet — wait for code verification.
  });
}

export function useCompleteVerification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: VerificationCompleteInput) => completeVerification(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROFILE_KEY }),
  });
}
